import NiceModal from "@ebay/nice-modal-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Grid, HStack, VStack } from "../../../../../styled-system/jsx";
import { Button } from "../../../../components/core/basic/Button";
import Loading from "../../../../components/core/blocks/Loading";
import { Checkbox } from "../../../../components/fields/base/Checkbox";
import { Switch } from "../../../../components/fields/base/Switch";
import { useSpotlightVideos } from "../../api/useSpotlightVideos";
import { useUpdateSpotlightExtension } from "../../api/useUpdateSpotlightExtension";
import { SpotlightVideoFormModal, type SpotlightVideoFormData } from "./SpotlightVideoForm";
import { SpotlightVideoSummary, type SpotlightVideo } from "./SpotlightVideoSummary";
import { snakeCaseToSentenceCase } from "../../../../utils/formatText";
import { listFormatter } from "../../../../utils/listFormatter";
import {
  FaBan,
  FaCheck,
  FaCircleCheck,
  FaEllipsis,
  FaPlus,
  FaQuestion,
  FaTriangleExclamation,
  FaVideo,
} from "react-icons/fa6";
import { HoverInfo } from "../../../../components/core/basic/Tooltip";
import { css } from "../../../../../styled-system/css";

type Props = {
  asin: string;
  adId: number;
};

type FormValues = {
  optimizeText: boolean;
  state: "enabled" | "paused";
};

const SpotlightVideoSection = ({ adId, asin }: Props) => {
  const { data, isLoading } = useSpotlightVideos(adId, asin);
  const { t } = useTranslation(["advertising"]);

  const { videos, deliveryStatus, deliveryReasons, moderationStatus, policyViolations } = data ?? { videos: [] };

  const updateSpotlightExtensionMutation = useUpdateSpotlightExtension(adId);

  const { watch, setValue, handleSubmit } = useForm<FormValues>({
    values: { optimizeText: data?.optimizeText ?? false, state: data?.state ?? "enabled" },
  });

  const optimizeText = watch("optimizeText");
  const state = watch("state");

  const onSubmit = (values: FormValues) => {
    updateSpotlightExtensionMutation.mutate({ asin, videos, optimizeText: values.optimizeText, state: values.state });
  };

  const updateVideos = async (updatedVideos: SpotlightVideo[]) =>
    await updateSpotlightExtensionMutation.mutateAsync({
      asin,
      videos: updatedVideos,
    });

  const handleVideoSubmit = async (formData: SpotlightVideoFormData, assetId?: string) => {
    const video: SpotlightVideo = {
      assetId: formData.assetId,
      assetVersion: formData.assetVersion,
      headline: formData.headline,
      description: formData.description,
      adGroupIds: formData.adGroups.length ? formData.adGroups : undefined,
    };

    if (assetId != null) {
      await updateVideos(videos.map((v) => (v.assetId === assetId ? { ...v, ...video } : v)));
    } else {
      await updateVideos([...videos, video]);
    }
  };

  const openForm = (assetId?: string) => {
    const video = assetId != null ? videos.find((v) => v.assetId === assetId) : undefined;
    void NiceModal.show(SpotlightVideoFormModal, {
      asin,
      adId,
      video,
      onSubmit: async (formData: SpotlightVideoFormData) => await handleVideoSubmit(formData, assetId),
    });
  };

  const handleDelete = async (assetId: string) => {
    try {
      await updateVideos(videos.filter((v) => v.assetId !== assetId));
    } catch {
      return;
    }
  };

  if (isLoading) {
    return <Loading fullPage={false} height={"150px"} />;
  }

  if (videos.length === 0) {
    return (
      <VStack alignItems="center" width="100%" gap="medium" paddingY="xlarge">
        <FaVideo className={css({ fontSize: "2xl", color: "gray.400" })} />
        <VStack gap="xsmall" alignItems="center">
          <span className={css({ fontWeight: 600, fontSize: "large" })}>
            {t(($) => $.advertising["No Spotlight Videos"])}
          </span>
          <span className={css({ color: "gray.500" })}>
            {t(($) => $.advertising["Add a video to showcase your product with a Spotlight Video ad."])}
          </span>
        </VStack>
        <Button onPress={() => openForm()}>
          <FaPlus /> {t(($) => $.advertising["Add Video"])}
        </Button>
      </VStack>
    );
  }

  return (
    <VStack alignItems="flex-start" width="100%" gap="medium">
      <Grid gridTemplateColumns="repeat(3, 1fr)" width="100%" gap="large" paddingTop="large">
        <div>
          <Button onPress={() => openForm()}>
            <FaPlus /> {t(($) => $.advertising["Add Video"])}
          </Button>
        </div>
        <VStack gap="small" justifyContent="center">
          <HStack gap="small" alignItems="center">
            <span>
              <strong>{t(($) => $.advertising["Delivery Status"])}</strong>: {snakeCaseToSentenceCase(deliveryStatus)}
            </span>

            {deliveryReasons?.length ?
              <HoverInfo
                iconCss={{
                  color:
                    deliveryStatus === "DELIVERING" ? "green"
                    : deliveryStatus === "NOT_DELIVERING" ? "yellow"
                    : "gray",
                  display: "inline",
                }}
                icon={
                  deliveryStatus === "DELIVERING" ? <FaCheck />
                  : deliveryStatus === "NOT_DELIVERING" ?
                    <FaTriangleExclamation />
                  : <FaQuestion />
                }
              >
                {t(($) => $.advertising["Reason: {reasons}"], {
                  reasons: listFormatter.format(deliveryReasons.map((reason) => snakeCaseToSentenceCase(reason))),
                })}
              </HoverInfo>
            : null}
          </HStack>

          {moderationStatus && (
            <HStack gap="small" alignItems="center">
              <span>
                <strong>{t(($) => $.advertising["Moderation Status"])}</strong>:{" "}
                {snakeCaseToSentenceCase(moderationStatus)}
              </span>
              <HoverInfo
                iconCss={{
                  color:
                    moderationStatus === "APPROVED" ? "green"
                    : moderationStatus === "REJECTED" ? "red"
                    : moderationStatus === "PENDING_REVIEW" ? "yellow"
                    : "gray",
                  display: "inline",
                }}
                icon={
                  moderationStatus === "APPROVED" ? <FaCircleCheck />
                  : moderationStatus === "REJECTED" ?
                    <FaBan />
                  : moderationStatus === "PENDING_REVIEW" ?
                    <FaTriangleExclamation />
                  : <FaEllipsis />
                }
              >
                {policyViolations?.length ?
                  <>
                    {t(($) => $.advertising["Policy violations:"])}
                    <ul className={css({ paddingLeft: "medium", margin: 0 })}>
                      {policyViolations.map((violation: string) => (
                        <li key={violation}>{snakeCaseToSentenceCase(violation)}</li>
                      ))}
                    </ul>
                  </>
                : t(($) => $.advertising["Moderation status: {status}"], {
                    status: snakeCaseToSentenceCase(moderationStatus),
                  })
                }
              </HoverInfo>
            </HStack>
          )}
        </VStack>
        <HStack gap="medium" alignItems="center" justifyContent="flex-end">
          <Checkbox
            value={optimizeText}
            label={t(($) => $.advertising["Optimize Text"])}
            onChange={(checked) => setValue("optimizeText", checked)}
          />
          <Switch
            isSelected={state === "enabled"}
            onChange={(isSelected) => setValue("state", isSelected ? "enabled" : "paused")}
          >
            {state === "enabled" ? t(($) => $.advertising["Enabled"]) : t(($) => $.advertising["Paused"])}
          </Switch>
          <Button
            onPress={async () => await handleSubmit(onSubmit)()}
            isDisabled={updateSpotlightExtensionMutation.isPending}
            isPending={updateSpotlightExtensionMutation.isPending}
          >
            {t(($) => $.advertising["Save Changes"])}
          </Button>
        </HStack>
      </Grid>
      <SpotlightVideoSummary videos={videos} onEdit={(assetId) => openForm(assetId)} onDelete={handleDelete} />
    </VStack>
  );
};

export { SpotlightVideoSection };
